import React, { useState } from 'react';

// --- Material-UI Core Components ---
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';

// --- Argon Design System Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// --- FAKE DATA FOR DEMONSTRATION ---
// This data is used as a fallback if product.consumptionSchedules is not available.
// It will be ignored once real data is passed to the component.
const FAKE_CONSUMPTION_SCHEDULES = [
  {
    consumptionSchedules: {
      sfId: 'fake-cs-01',
      name: 'Monthly Data Usage',
      id: 'fake-cs-01',
      fields: {
        Name: 'Monthly Data Usage',
        Type: 'Slab',
        RatingMethod: 'Tier',
        UnitOfMeasure: 'GB',
        BillingTerm: '1',
        BillingTermUnit: 'Month',
        IsActive: 'true',
        Description: 'Standard data consumption plan for mobile devices.',
      },
    },
    consumptionRates: [
      {
        sfId: 'fake-cr-01-a',
        id: 'fake-cr-01-a',
        fields: { ProcessingOrder: '1', LowerBound: '0', UpperBound: '10', Price: '5.00', PricingMethod: 'FlatFee' },
      },
      {
        sfId: 'fake-cr-01-b',
        id: 'fake-cr-01-b',
        fields: { ProcessingOrder: '2', LowerBound: '10', UpperBound: null, Price: '0.50', PricingMethod: 'PerUnit' },
      },
    ],
  },
  {
    consumptionSchedules: {
      sfId: 'fake-cs-02',
      name: 'API Call Volume',
      id: 'fake-cs-02',
      fields: {
        Name: 'API Call Volume',
        Type: 'Tier',
        RatingMethod: 'Tier',
        UnitOfMeasure: 'Calls',
        BillingTerm: '1',
        BillingTermUnit: 'Month',
        IsActive: 'true',
        Description: 'Pay-as-you-go pricing for API calls.',
      },
    },
    consumptionRates: [
      {
        sfId: 'fake-cr-02-a',
        id: 'fake-cr-02-a',
        fields: { ProcessingOrder: '1', LowerBound: '0', UpperBound: '10000', Price: '0.00', PricingMethod: 'PerUnit' },
      },
      {
        sfId: 'fake-cr-02-b',
        id: 'fake-cr-02-b',
        fields: {
          ProcessingOrder: '2',
          LowerBound: '10000',
          UpperBound: '100000',
          Price: '0.001',
          PricingMethod: 'PerUnit',
        },
      },
      {
        sfId: 'fake-cr-02-c',
        id: 'fake-cr-02-c',
        fields: {
          ProcessingOrder: '3',
          LowerBound: '100000',
          UpperBound: null,
          Price: '0.0005',
          PricingMethod: 'PerUnit',
        },
      },
    ],
  },
];
// --- END OF FAKE DATA ---

// Helper function to filter an object's keys based on an allowed list
const filterObjectByKeys = (raw, allowed) => {
  if (!raw || !allowed || !Array.isArray(allowed)) return {};
  return Object.keys(raw)
    .filter((key) => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

function RateCardsTab({ product, renderFieldsProp, basicFields }) {
  const [expandedSchedule, setExpandedSchedule] = useState({});

  // Use real data if available, otherwise fall back to the fake data for demonstration.
  const schedules = product?.consumptionSchedules || FAKE_CONSUMPTION_SCHEDULES;

  if (!schedules || schedules.length === 0) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Rate Cards available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleScheduleClick = (scheduleId) => {
    setExpandedSchedule((prevState) => ({
      ...prevState,
      [scheduleId]: !prevState[scheduleId],
    }));
  };

  return (
    <ArgonBox p={3}>
      {schedules.map((scheduleData) => {
        const schedule = scheduleData.consumptionSchedules;
        const rates = scheduleData.consumptionRates;
        const scheduleId = schedule.sfId || schedule.id;
        const hasRates = rates && rates.length > 0;

        // Filter the schedule's main fields based on the visibility config
        const fieldsToDisplay = filterObjectByKeys(schedule.fields, basicFields);

        return (
          <Card key={scheduleId} sx={{ marginBottom: '1rem' }}>
            <ArgonBox mb={0.5} mt={0.5}>
              {/* --- Clickable Header for each Rate Card --- */}
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleScheduleClick(scheduleId)}
                style={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                  {schedule.name || 'Unnamed Rate Card'}
                </ArgonTypography>
                <IconButton
                  aria-label="expand schedule"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent parent onClick from firing again
                    handleScheduleClick(scheduleId);
                  }}
                >
                  <Icon>{expandedSchedule[scheduleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              {/* --- Collapsible Content for the Rate Card --- */}
              <Collapse in={expandedSchedule[scheduleId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {/* Render general rate card fields from visibility config */}
                  {renderFieldsProp(fieldsToDisplay)}

                  {/* --- Table for Consumption Rates --- */}
                  {hasRates && (
                    <ArgonBox mt={3}>
                      <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1} ml={1}>
                        Consumption Rates
                      </ArgonTypography>
                      <TableContainer component={Paper}>
                        <Table size="small" aria-label="consumption rates">
                          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
                            <TableRow>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Lower Bound
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Upper Bound
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Price
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Pricing Method
                                </ArgonTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rates
                              .sort((a, b) => a.fields.ProcessingOrder - b.fields.ProcessingOrder)
                              .map((rate) => (
                                <TableRow key={rate.sfId || rate.id}>
                                  <TableCell>{rate.fields?.LowerBound ?? 'N/A'}</TableCell>
                                  <TableCell>{rate.fields?.UpperBound ?? '∞'}</TableCell>
                                  <TableCell>{rate.fields?.Price ?? 'N/A'}</TableCell>
                                  <TableCell>{rate.fields?.PricingMethod ?? 'N/A'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </ArgonBox>
                  )}
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default RateCardsTab;
